/**
 * Oppbevaring.  OA-012, 2026-10-01.
 *
 * How long the mosque keeps each category of personal data. No period comes
 * from the platform: every category starts undecided and switched off, and
 * nothing is deleted until a super admin has
 *   1. set a period (in days, never below the category's floor),
 *   2. run a dry run and read the count,
 *   3. written down the decision it rests on, and switched it to "Håndhev".
 * Changing the period drops back to a dry run. Admins can read it; only a
 * super admin can change it.
 */
import React, { useState } from 'react';
import { errText, patch, post, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { osloTime } from '../lib/format';
import { Btn, Card, Field, LoadError, Loading, Msg, Tag, TwoStep, type TagTone } from '../components/ui';
import { useWorkHub } from '../components/WorkCard';

const MODE_NO: Record<string, [string, TagTone]> = {
  off: ['av', 'grey'], dry_run: ['prøvekjøring', 'gold'], enforce: ['håndheves', 'green'],
};

function Row({ c, canChange, onSaved, say }: { c: Body; canChange: boolean; onSaved: (items: Body[]) => void; say: (t: string, tone?: 'ok' | 'err') => void }) {
  const p = c.policy || {};
  const [days, setDays] = useState<string>(p.periodDays != null ? String(p.periodDays) : '');
  const [basis, setBasis] = useState<string>(p.basis || '');
  const [busy, setBusy] = useState(false);
  const save = async (body: Body, done: string) => {
    setBusy(true);
    const r = await patch(`/retention/${encodeURIComponent(c.key)}`, body);
    setBusy(false);
    if (!r.ok) return say(errText(r.body), 'err');
    say(done, 'ok');
    onSaved(r.body.items);
  };
  const dry = async () => {
    setBusy(true);
    const r = await post(`/retention/${encodeURIComponent(c.key)}/dry-run`);
    setBusy(false);
    if (!r.ok) return say(errText(r.body), 'err');
    const x = r.body.result;
    say(`Prøvekjøring: ${x.candidates} ville blitt slettet${x.blocked ? `, ${x.blocked} beholdes (vedlegg har egen dato)` : ''}. Ingenting er slettet.`, 'ok');
    onSaved(r.body.items);
  };
  const [label, tone] = MODE_NO[p.mode] || [p.mode, 'grey' as TagTone];
  const last = c.lastRun;
  const dryDone = last && last.mode === 'dry_run' && last.policyVersion === p.version && last.periodDays === p.periodDays && !last.error;
  return (
    <tr>
      <td><strong>{c.label}</strong>{c.note && <span className="note" style={{ display: 'block' }}>{c.note}</span>}</td>
      <td>
        {c.fixed ? <span className="note">{c.fixed}</span>
          : !c.executable ? <Tag tone="grey">ikke tilgjengelig ennå</Tag>
          : <><Tag tone={tone}>{label}</Tag> {c.decided ? <span className="note">{p.periodDays} dager</span> : <span className="note">ikke bestemt</span>}</>}
        {last && <span className="note" style={{ display: 'block' }}>
          Sist {last.mode === 'enforce' ? 'kjørt' : 'prøvekjørt'} {osloTime(last.at)}: {last.candidates} gamle{last.mode === 'enforce' ? `, ${last.removed} slettet` : ''}{last.blocked ? `, ${last.blocked} beholdt` : ''}{last.error ? ` — feil: ${last.error}` : ''}</span>}
      </td>
      <td>{canChange && c.executable && (
        <div style={{ display: 'grid', gap: 6, minWidth: 240 }}>
          <Field label="Dager" note={`Minst ${c.minDays}.`}>{(id, d) => (
            <input id={id} aria-describedby={d} inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))} />)}</Field>
          <Btn ghost disabled={busy || !days} onClick={() => save({ periodDays: Number(days), mode: 'dry_run' }, 'Lagret som prøvekjøring. Ingenting slettes.')}>Lagre som prøvekjøring</Btn>
          {p.mode !== 'off' && <Btn ghost disabled={busy} onClick={dry}>Prøvekjør nå</Btn>}
          {p.mode === 'dry_run' && (
            <>
              <Field label="Vedtaket det bygger på" note="F.eks. «Styrevedtak 2026-11-01, sak 12» eller juridisk vurdering.">{(id, d) => (
                <input id={id} aria-describedby={d} maxLength={300} value={basis} onChange={(e) => setBasis(e.target.value)} />)}</Field>
              <TwoStep label="Håndhev" confirmLabel={`Slett data eldre enn ${p.periodDays} dager — trykk igjen`}
                disabled={busy || !basis.trim() || !dryDone}
                onConfirm={() => save({ mode: 'enforce', basis }, 'Håndheves fra neste daglige kjøring.')} />
              {!dryDone && <span className="note">Kjør en prøvekjøring med denne tiden først.</span>}
            </>
          )}
          {p.mode !== 'off' && <Btn ghost disabled={busy} onClick={() => save({ mode: 'off' }, 'Slått av. Ingenting slettes.')}>Slå av</Btn>}
        </div>)}</td>
    </tr>
  );
}

export function Retention() {
  const { data, error, reload } = useLoad<Body>('/retention');
  const [msg, say] = useMsg();
  const [items, setItems] = useState<Body[] | null>(null);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const list = items || data.items || [];
  return (
    <>
      <Card title="Oppbevaring" hint="Hvor lenge moskeen beholder personopplysninger. Ingen tider er satt av plattformen: ingenting slettes før moskeen har bestemt en tid, prøvekjørt den og slått den på.">
        <Msg text={msg.text} tone={msg.tone} />
        <table className="t">
          <thead><tr><th scope="col">Kategori</th><th scope="col">Status</th><th scope="col"><span className="sr-only">Endre</span></th></tr></thead>
          <tbody>{list.map((c: Body) => <Row key={c.key + ':' + (c.policy && c.policy.version)} c={c} canChange={Boolean(data.canChange)} onSaved={setItems} say={say} />)}</tbody>
        </table>
      </Card>
      <WorkRetention />
    </>
  );
}

const BANDS: [string, string][] = [['under90', 'under 90 dager'], ['90to365', '90 dager–1 år'], ['1to2y', '1–2 år'], ['over2y', 'over 2 år']];
/**
 * The staff work hub's data, by age (Batch 1 hardening, 2026-10-02) — READ-ONLY. There is no retention category for it
 * yet: the mosque has to decide a period first (MKK_ADOPTION_BATCH_1.md §12.4). Counts and ages, never a title or a name;
 * active work is counted, never aged, and could never be removed. Shown when the mosque has the work hub, or has its data.
 */
function WorkRetention() {
  const hub = useWorkHub();
  const { data, error, reload } = useLoad<Body>('/work/retention');
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return null;
  const any = data.tasks.active + data.tasks.finished + data.comments.total + data.checklists.open + data.checklists.finished + data.planner.active + data.planner.cancelled;
  if (!hub && !any) return null;
  const rows: [string, Body, string][] = [['Ferdige og avbrutte oppgaver', data.tasks, `${data.tasks.active} pågår`],
    ['Kommentarer på ferdige oppgaver', data.comments, `${data.comments.total - data.comments.onFinishedTasks} på pågående`],
    ['Ferdige og avbrutte sjekklister', data.checklists, `${data.checklists.open} pågår · ${data.checklists.ticks} avkryssinger i de ferdige`],
    ['Avlyste punkter i årshjulet', data.planner, `${data.planner.active} ${data.planner.active === 1 ? 'aktivt' : 'aktive'}`]];
  return (
    <Card title="Oppgaver og sjekklister" hint="Ingen lagringstid er bestemt for de ansattes arbeid. Her ser du hvor mye som er lagret og hvor gammelt det er, så moskeen kan bestemme. Ingenting slettes herfra, og pågående arbeid kan aldri slettes.">
      <div className="card-body">
        <p><Tag tone="gold">Lagringstid ikke bestemt</Tag></p>
        <table className="t">
          <caption className="vh">Ferdig arbeid etter alder, per {data.asOf}</caption>
          <thead><tr><th scope="col">Hva</th><th scope="col">Antall</th>{BANDS.map(([k, l]) => <th key={k} scope="col">{l}</th>)}<th scope="col">Eldste</th><th scope="col">Ikke med</th></tr></thead>
          <tbody>{rows.map(([label, r, rest]) => (
            <tr key={label}><th scope="row">{label}</th><td>{r.finished}</td>{BANDS.map(([k]) => <td key={k}>{r.byAge[k]}</td>)}
              <td>{r.oldestDays == null ? '—' : `${r.oldestDays} dager`}</td><td>{rest}</td></tr>
          ))}</tbody>
        </table>
      </div>
    </Card>
  );
}
